// 牌局儀式動畫的純時序規則。
// Phaser 畫面元件只負責依這份順序播放，測試可直接驗證玩家看見的行為。

import { RoundSummaryKind, type TRoundSummary } from '@core/summary';
import type { TTile } from '@core/tile';
import type { TSeat } from '@core/types';

const MARK_UPSCALE_FACTOR = 4; // Upscayl 動畫素材相對原 APP 素材的邊長倍率

export const WinCeremonyKind = {
  SelfDraw: 'self-draw',
  DiscardWin: 'discard-win',
} as const;

export type TWinCeremonyKind = (typeof WinCeremonyKind)[keyof typeof WinCeremonyKind];

export const GameDrawCeremonyKind = {
  Draw: 'draw',
} as const;

export type TGameDrawCeremonyKind =
  (typeof GameDrawCeremonyKind)[keyof typeof GameDrawCeremonyKind];

/** 4× 動畫素材切回原始素材後，換算成相同畫面尺寸所需的 scale。 */
export const baseMarkScaleOf = (upscaledScale: number): number =>
  upscaledScale * MARK_UPSCALE_FACTOR;

/** 把一局胡牌結果整理成畫面動畫所需的贏家／放槍者計畫；流局不播放胡牌儀式。 */
export function buildWinCeremonyPlan(summary: TRoundSummary): {
  readonly kind: TWinCeremonyKind;
  readonly winnerSeats: TSeat[];
  readonly loserSeats: TSeat[];
  /** 放槍者門前要保留的獨立胡張；同一位放槍者只顯示一張。 */
  readonly discardedTiles: Array<{
    readonly fromSeat: TSeat;
    readonly tile: TTile;
  }>;
} | null {
  if (summary.kind !== RoundSummaryKind.Hu || summary.results.length === 0) return null;

  const discardedTileBySeat = new Map<TSeat, TTile>();
  for (const result of summary.results) {
    if (result.isSelfDraw || result.loserSeat === null) continue;
    if (!discardedTileBySeat.has(result.loserSeat)) {
      discardedTileBySeat.set(result.loserSeat, result.winTile);
    }
  }

  return {
    kind: summary.results.some((result) => result.isSelfDraw)
      ? WinCeremonyKind.SelfDraw
      : WinCeremonyKind.DiscardWin,
    winnerSeats: [...new Set(summary.results.map((result) => result.seat))],
    loserSeats: [
      ...new Set(
        summary.results
          .map((result) => result.loserSeat)
          .filter((seat): seat is NonNullable<typeof seat> => seat !== null)
      ),
    ],
    discardedTiles: [...discardedTileBySeat].map(([fromSeat, tile]) => ({ fromSeat, tile })),
  };
}

/** 流局先在牌桌內播放原版流局演出；胡牌則交由 CWinCeremony。 */
export function buildGameDrawCeremonyPlan(summary: TRoundSummary): {
  readonly kind: TGameDrawCeremonyKind;
} | null {
  if (summary.kind !== RoundSummaryKind.Draw) return null;
  return { kind: GameDrawCeremonyKind.Draw };
}

/** 原版中央自摸動畫完成後，只有自摸贏家門前要留下「自摸」脈動印記。 */
export function selfDrawSeatMarkSeats(
  plan: NonNullable<ReturnType<typeof buildWinCeremonyPlan>>
): TSeat[] {
  return plan.kind === WinCeremonyKind.SelfDraw ? [...plan.winnerSeats] : [];
}

/** 控制胡牌儀式的點擊閘門：動畫完成前拒絕，完成後只允許一次。 */
export class CWinCeremonyGate {
  private animationComplete = false;
  private consumed = false;

  begin(): void {
    this.animationComplete = false;
    this.consumed = false;
  }

  markAnimationComplete(): void {
    this.animationComplete = true;
  }

  requestContinue(): boolean {
    if (!this.animationComplete || this.consumed) return false;
    this.consumed = true;
    return true;
  }
}
